import { ref, onMounted, onUpdated, onUnmounted, type Ref } from 'vue';

/**
 * Detects whether an element's text content is truncated (overflowing).
 * Binds the returned `el` ref to the element you want to observe.
 *
 * @example
 * const { el, isTruncated } = useTruncation();
 * // <span ref="el" v-tooltip="isTruncated ? text : undefined">{{ text }}</span>
 */
type TruncationAxis = 'horizontal' | 'vertical' | 'both';

export const useTruncation = (
  axis: TruncationAxis = 'horizontal',
): { el: Ref<HTMLElement | null>; isTruncated: Ref<boolean> } => {
  const el = ref<HTMLElement | null>(null);
  const isTruncated = ref(false);

  const check = () => {
    if (el.value) {
      const horizontal = el.value.scrollWidth > el.value.clientWidth;
      const vertical = el.value.scrollHeight > el.value.clientHeight;
      isTruncated.value =
        (axis !== 'vertical' && horizontal) || (axis !== 'horizontal' && vertical);
    }
  };

  onMounted(() => {
    check();
    window.addEventListener('resize', check);
  });

  onUpdated(check);

  onUnmounted(() => {
    window.removeEventListener('resize', check);
  });

  return { el, isTruncated };
};
